import { endOfDay, parse, isValid, startOfDay } from 'date-fns';
import type { FilterOperation } from '@/hooks/useConnectionData';
import {
  DATE_RANGE_FILTER_SEPARATOR,
  parseFilterableDate,
} from '@/components/data-table/filters/dateRangeFilterUtils';

export interface ColumnFilter {
  column: string;
  value: string;
  operation: FilterOperation;
}

/** Client-side column filter matching inventory / connection table behavior. */
export function applyColumnFilters<T extends Record<string, unknown>>(
  rows: T[],
  filters: ColumnFilter[],
): T[] {
  if (filters.length === 0) {
    return rows;
  }

  return rows.filter((item) =>
    filters.every((filter) => {
      const cellValue = item[filter.column];

      if (filter.operation === 'is_blank') {
        return cellValue === null || cellValue === undefined || cellValue === '';
      }
      if (filter.operation === 'is_not_blank') {
        return cellValue !== null && cellValue !== undefined && cellValue !== '';
      }

      const cellStr = String(cellValue ?? '').toLowerCase();
      const filterStr = String(filter.value ?? '').toLowerCase();
      const cellNum = parseFloat(String(cellValue ?? ''));
      const filterNum = parseFloat(filter.value);

      switch (filter.operation) {
        case 'equals':
          return !Number.isNaN(cellNum) && !Number.isNaN(filterNum)
            ? cellNum === filterNum
            : cellStr === filterStr;
        case 'not_equals':
          return !Number.isNaN(cellNum) && !Number.isNaN(filterNum)
            ? cellNum !== filterNum
            : cellStr !== filterStr;
        case 'contains':
        case 'like':
          return cellStr.includes(filterStr);
        case 'not_like':
          return !cellStr.includes(filterStr);
        case 'startsWith':
          return cellStr.startsWith(filterStr);
        case 'endsWith':
          return cellStr.endsWith(filterStr);
        case 'gt':
          return !Number.isNaN(cellNum) && !Number.isNaN(filterNum) && cellNum > filterNum;
        case 'gte':
          return !Number.isNaN(cellNum) && !Number.isNaN(filterNum) && cellNum >= filterNum;
        case 'lt':
          return !Number.isNaN(cellNum) && !Number.isNaN(filterNum) && cellNum < filterNum;
        case 'lte':
          return !Number.isNaN(cellNum) && !Number.isNaN(filterNum) && cellNum <= filterNum;
        case 'between': {
          const [startStr, endStr] = filter.value.split(DATE_RANGE_FILTER_SEPARATOR);
          if (!startStr || !endStr) return true;
          const cellDate = parseFilterableDate(cellValue);
          if (!cellDate) return false;
          const rangeStart = parse(startStr, 'yyyy-MM-dd', new Date());
          const rangeEnd = parse(endStr, 'yyyy-MM-dd', new Date());
          if (!isValid(rangeStart) || !isValid(rangeEnd)) return true;
          const cellTime = cellDate.getTime();
          return (
            cellTime >= startOfDay(rangeStart).getTime() &&
            cellTime <= endOfDay(rangeEnd).getTime()
          );
        }
        default:
          return true;
      }
    }),
  );
}
